import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setEmailVerificationRequired(!response.data.email_verified);
      
      // Also fetch subscription status
      try {
        const subResponse = await axios.get(`${API_BASE}/api/billing/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(subResponse.data);
      } catch (subErr) {
        console.log('No subscription data');
      }
    } catch (error) {
      console.error('Auth error:', error);
      // Token invalid/expired
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Setup axios interceptor to handle verification errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const code = error.response?.data?.detail?.code;
        // Handle all verification-related error codes
        if (code === 'EMAIL_NOT_VERIFIED' || 
            code === 'EMAIL_VERIFICATION_REQUIRED' ||
            code === 'VERIFICATION_EXPIRED') {
          setEmailVerificationRequired(true);
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Signup - creates pending account, does NOT return token
  const signup = async (email, password, referralCode = null) => {
    let url = `${API_BASE}/api/auth/signup`;
    if (referralCode) {
      url += `?ref=${encodeURIComponent(referralCode)}`;
    }
    
    const response = await axios.post(url, {
      email,
      password
    });
    
    // New signup returns: { success, message, next, email }
    // No token - user must verify email first
    const { success, message, next, email: userEmail } = response.data;
    
    // Don't set token or user - they need to verify first
    return { success, message, next, email: userEmail };
  };

  // Login
  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE}/api/auth/login`, {
      email,
      password
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('auth_token', access_token);
    setToken(access_token);
    setUser(userData);
    setEmailVerificationRequired(!userData.email_verified);
    
    return userData;
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    setSubscription(null);
    setEmailVerificationRequired(false);
  };

  // Verify email (simple click)
  const verifyEmail = async () => {
    if (!token) throw new Error('Not authenticated');
    
    await axios.post(`${API_BASE}/api/auth/verify-click`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    // Refresh user
    await fetchUser();
  };

  // Resend verification email
  const resendVerification = async (email) => {
    const targetEmail = email || user?.email;
    if (!targetEmail) throw new Error('No email provided');
    
    await axios.post(`${API_BASE}/api/auth/resend-verification`, {
      email: targetEmail
    });
  };

  // Refresh user data
  const refreshUser = async () => {
    await fetchUser();
  };

  // Check if user has premium access
  const isPremium = user?.plan === 'pro' && ['active', 'trialing'].includes(user?.subscription_status);
  const isPro = isPremium; // Alias for convenience

  // Check if user is authenticated
  const isAuthenticated = !!user;
  
  // Check if email is verified
  const isEmailVerified = user?.email_verified === true;

  const value = {
    user,
    token,
    loading,
    subscription,
    isAuthenticated,
    isPremium,
    isPro, // Alias
    isEmailVerified,
    emailVerificationRequired,
    signup,
    login,
    logout,
    verifyEmail,
    resendVerification,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
